import Form from "components/Form/form";
import { CheckCircleIcon, XIcon } from "@heroicons/react/solid";
import { useState, useEffect } from "react";
import { analytics } from "utilities/analytics";
import { LinkButton } from "components/Standalone/Button";
import { OMC_SUPPORT_CONTACT_URL } from "utilities/supportContact";
import { forgotPasswordHref, signInWithOmciHref } from "utilities/signInLinks";

// "Sign in with OMCi" (Cognito) is the sign-in route; the email/password form is
// the previous login kept while accounts migrate. Set NEXT_PUBLIC_LEGACY_LOGIN="false"
// to remove it once the agreed rollout retires it — no date is implied on the page.
const LEGACY_LOGIN_ENABLED = process.env.NEXT_PUBLIC_LEGACY_LOGIN !== "false";

/**
 * The sign-in page: OMC ship-mark, one card (max 448px, 16px page margins on
 * phones) with "Sign in with OMCi" as the primary action, the support route, and
 * below a divider the "Previous login" with its retirement notice. A valid
 * session never sees this: Form's session check sends it on to the Launch Pad
 * resolver, which also returns a page requested before sign-in (SPEC-007).
 * `children` (the version label) render after the card, inside the column.
 */
export default function FormContainer({ children }) {
    const [passwordReset, setPasswordReset] = useState(false);
    const ssoHref = signInWithOmciHref();
    const trackSsoStarted = () => {
        try {
            analytics.track("sso_started", { sso_provider: "cognito" }, { sendInstantly: true });
        } catch {
            /* fail-open */
        }
    };
    useEffect(() => {
        if (window.localStorage.getItem("reset_password") !== undefined && window.localStorage.getItem("reset_password") !== null) {
            setPasswordReset(true);
            window.localStorage.removeItem("reset_password");
        }
    }, []);

    return (
        <div className="flex min-h-screen flex-col items-center bg-canvas px-4 pt-10 pb-16 sm:pt-11">
            {/* The approved OMC ship-mark (the same instance as the Hub's Help rail), light and dark. */}
            <img className="h-10 w-auto dark:hidden" src="/logo/icon.svg" alt="OMC" />
            <img className="hidden h-10 w-auto dark:block" src="/logo/OMC_Logo_dark.svg" alt="OMC" />

            {passwordReset && (
                <div
                    role="status"
                    className="mt-6 flex w-full max-w-md items-start gap-3 rounded-omc border border-status-success-border bg-status-success-bg px-4 py-3 text-sm text-status-success-fg"
                >
                    <CheckCircleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                    <p className="flex-1 font-medium">Password has been reset successfully.</p>
                    <button
                        type="button"
                        className="-m-1 rounded-omc-sm p-1 hover:opacity-75 focus:outline-none focus-visible:ring-2 focus-visible:ring-line-focus"
                        onClick={() => setPasswordReset(false)}
                    >
                        <span className="sr-only">Dismiss</span>
                        <XIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                </div>
            )}

            <main className="mt-9 w-full max-w-md rounded-omc-lg border border-line-subtle bg-surface px-6 py-8 shadow-surface sm:p-8" data-testid="sign-in-card">
                <h1 className="text-2xl font-semibold leading-8 text-ink">Sign in</h1>

                <LinkButton variant="primary" size="medium" className="mt-6 w-full" href={ssoHref} onClick={trackSsoStarted} data-testid="sign-in-with-omci">
                    Sign in with OMCi
                </LinkButton>

                <p className="mt-4 text-sm leading-5 text-ink-secondary">Need help signing in?</p>
                <a
                    href={OMC_SUPPORT_CONTACT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-block rounded-omc-sm text-sm font-medium leading-5 text-ink-link hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-line-focus"
                    data-testid="contact-omc-support"
                >
                    Contact OMC support
                </a>

                {LEGACY_LOGIN_ENABLED && (
                    <section aria-labelledby="previous-login-heading" className="mt-6 border-t border-line-subtle pt-6">
                        <h2 id="previous-login-heading" className="text-lg font-semibold leading-7 text-ink">
                            Previous login
                        </h2>
                        <p className="mt-2 text-sm leading-5 text-ink-secondary">
                            This login will be retired. Use the new sign-in option above, or continue below during the transition.
                        </p>
                        <div className="mt-6">
                            <Form forgotPasswordHref={forgotPasswordHref()} />
                        </div>
                    </section>
                )}
            </main>
            {children}
        </div>
    );
}
